import React, {
    useCallback,
    useState
} from 'react';
import PropTypes from 'prop-types';

import {
    useGetFocusedInstanceIdValue
} from '../../../hooks/widgetConfiguration.js';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';

import MetricTableHeader from '../../../components/table/MetricTableHeader.jsx';
import MetricTableCell from '../../../components/table/MetricTableCell.jsx';
import RIQModal from '../../../components/RIQModal.jsx';

import Empty from './Empty.jsx';

import {
    RIQPopover,
    RIQInfoPopover
} from '../../../components/RIQPopover.jsx';

import {
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import useCDNUrl from '../../../hooks/useCDNUrl.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import useListeningCurationFilterValues from '../hooks/useListeningCurationFilterValues.js';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

PostsTableComponent.propTypes = {
    instanceId: PropTypes.string.isRequired,
    limit: PropTypes.number,
    showOnlyPostsColumn: PropTypes.bool,
    showSinglePostModals: PropTypes.bool,
    pdfExportConfig: PropTypes.object,
    appSpecificExportConfig: PropTypes.object
};

function getSentimentClassName(sentiment) {
    let textColorClassName = 'tlw-text-gray-900';
    if (sentiment === 'Positive') {
        textColorClassName = 'tlw-text-green-400';
    } else if (sentiment === 'Negative') {
        textColorClassName = 'tlw-text-red-400';
    }

    return textColorClassName;
}

function makeDefaultTableHeader({columnDef, metricRequests}) {
    const metricRequest = metricRequests[columnDef.metrics[0]];
    const metricName = RivalIQ.metrics.new.MetricRegistry.getName(metricRequest, true);

    return (
        <MetricTableHeader
            key={columnDef.idx}
            title={metricName}
            description={columnDef.description}
            alignment={MetricTableHeader.Alignment.CENTER}
        />
    );
}

function makeDefaultTableCell({columnDef, metricRequests, rowData, formattersByFieldName}) {
    const metric = columnDef.metrics[0];
    const rawValue = _.last(rowData[metric]);

    const metricRequest = metricRequests[metric];
    const metricName = RivalIQ.metrics.new.MetricRegistry.getName(metricRequest, true);

    return <MetricTableCell
        key={columnDef.idx}
        abbreviatedValue={formattersByFieldName[metric].abbreviated(rawValue)}
        metricName={metricName}
        expandedValue={formattersByFieldName[metric].expanded(rawValue)}
        alignment={MetricTableCell.Alignment.RIGHT}
    />;
}

function makePublishedAtTableCell(timezone, {columnDef, rowData}) {
    const metric = columnDef.metrics[0];
    // This is the published at date in the timezone that this metric was requested with.
    const publishedAt = moment.tz(_.last(rowData[metric]), moment.ISO_8601, timezone);

    return (
        <td
            key={columnDef.idx}
            className='tlw-min-w-[108px] tlw-p-4 tlw-text-center'
        >
            <div
                className='tlw-whitespace-nowrap tlw-text-md tlw-font-bold'>
                {publishedAt.format('MMM D, YYYY')}
            </div>
        </td>
    );
}

function PostSentiment({sentiment, mode, postDeleted}) {
    if (postDeleted) {
        if (mode === 'cell') {
            return (
                <div className={'tlw-whitespace-nowrap tlw-text-md'}>
                    --
                </div>
            );
        } else if (mode === 'modal') {
            return (
                <div className={'tlw-table-cell tlw-py-7 tlw-pl-7 tlw-text-right tlw-align-middle tlw-text-21 tlw-font-bold'}>
                    --
                </div>
            );
        }
    }

    const textColorClassName = getSentimentClassName(sentiment);

    if (mode === 'cell') {
        return (
            <div className={`tlw-whitespace-nowrap tlw-text-md ${textColorClassName}`}>
                {sentiment}
            </div>
        );
    } else if (mode === 'modal') {
        return (
            <div className={`tlw-table-cell tlw-py-7 tlw-pl-7 tlw-text-right tlw-align-middle tlw-text-21 tlw-font-bold ${textColorClassName}`}>
                {sentiment}
            </div>
        );
    }
}

function makePostSentimentTableCell({columnDef, rowData}) {
    const sentiment = _.last(rowData.postSentiment);
    const postDeleted = _.last(rowData.postDeleted);

    return (
        <td
            key={columnDef.idx}
            className='tlw-p-4 tlw-text-center tlw-font-normal'
        >
            <PostSentiment
                sentiment={sentiment}
                mode={'cell'}
                postDeleted={postDeleted}
            />
        </td>
    );
}

function PostAuthor({postAuthorName, postTitle, postForumName, postAuthorUsername, postAuthorLink, postDeleted}) {
    let postAuthorNode = <></>;
    const titleClassName = 'tlw-overflow-hidden tlw-text-ellipsis tlw-text-gray-900 hover_tlw-text-gray-900 tlw-font-bold tlw-inline-flex';

    if (postDeleted) {
        postAuthorNode = <div>&nbsp;</div>;
    } else if (postTitle) {

        postAuthorNode = <>
            <span
                className={titleClassName}
                style={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 1
                }}
            >
                {postTitle}
            </span>

            {postAuthorName &&
                <div
                    className='tlw-mb-2.5 tlw-text-sm tlw-font-normal tlw-text-gray-700'
                >
                    {postAuthorName}
                    {postForumName &&
                        <>
                            &nbsp;|&nbsp;{postForumName}
                        </>
                    }
                </div>
            }
        </>;

    } else if (postAuthorName) {
        if (postAuthorLink) {
            postAuthorNode =
            <>
                <span
                    className={titleClassName}
                >
                    {postAuthorName}
                    {postAuthorUsername &&
                    <>
                        &nbsp;
                        <a
                            href={postAuthorLink}
                            target='_blank'
                            rel='noreferrer'
                            className='tlw-overflow-hidden tlw-text-ellipsis'
                            style={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 1
                            }}
                        >
                            <span
                                className='tlw-text-md tlw-font-normal tlw-text-gray-900 hover_tlw-text-gray-900'
                            >
                                @{postAuthorUsername}
                            </span>
                        </a>
                    </>
                    }
                </span>
            </>;
        } else {
            postAuthorNode = <span
                className={titleClassName}
            >
                {postAuthorName}
            </span>;
        }
    } else if (postAuthorLink) {
        postAuthorNode = <div
            className={titleClassName}
        >
            {postAuthorLink}
        </div>;
    }

    return postAuthorNode;
}

function PostSource({postAuthorAvatarUrl, postAuthorName, postDomain, postSource, isWideDisplay}) {
    const widthClass = (isWideDisplay ? 'tlw-w-[58px]' : 'tlw-w-[45px]');
    const avatarHeightClass = (isWideDisplay ? 'tlw-h-[58px]' : 'tlw-h-[45px]');

    const defaultAvatarUrl = useCDNUrl('/images/default-avatar-white.png');
    const redditAvatarUrl = useCDNUrl('/images/reddit-snoo.png');

    if (!postAuthorAvatarUrl && postDomain === 'reddit.com') {
        postAuthorAvatarUrl = redditAvatarUrl;
    }

    postAuthorAvatarUrl = postAuthorAvatarUrl || defaultAvatarUrl;

    const [hasImgError, setHasImgError] = useState(false);
    const onAuthorAvatarImgError = useCallback(() => {
        setHasImgError(e => true);
    }, []);

    let authorAvatar = null;
    if (postAuthorAvatarUrl) {
        if (hasImgError) {
            authorAvatar = (
                <span
                    className={`tlw-flex tlw-cursor-default tlw-select-none tlw-items-center tlw-justify-center tlw-overflow-hidden tlw-rounded-sm tlw-bg-gray-300 tlw-font-bold tlw-uppercase ${widthClass} ${avatarHeightClass}`}
                    title={postAuthorName}
                >
                    {_.first(postAuthorName)}
                </span>
            );
        } else {
            authorAvatar = (
                <img
                    className={`tlw-overflow-hidden tlw-rounded-sm ${widthClass} ${avatarHeightClass}`}
                    onError={onAuthorAvatarImgError}
                    src={postAuthorAvatarUrl}
                    alt={postAuthorName}
                />
            );
        }
    }

    return (
        <div
            className={`tlw-flex tlw-flex-col tlw-gap-2 ${widthClass}`}
        >
            {authorAvatar}
            {postSource &&
            <div className={`tlw-overflow-hidden tlw-text-ellipsis tlw-rounded-sm tlw-bg-gray-700 tlw-text-center tlw-text-10 tlw-font-bold tlw-text-white ${widthClass}`}
                title={postSource}>
                {postSource}
            </div>}
        </div>
    );
}

function PostDigest({postDigest, mode, postDeleted}) {
    if (postDeleted) {
        return null;
    }

    if (mode === 'cell') {
        return (
            <div
                style={{
                    // Tailwind doesn't support these as they're still in draft mode (though all browsers support them, even with the webkit prefix).
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 3,
                    overflow: 'hidden',

                    // This style is not yet supported by tailwind.
                    overflowWrap: 'anywhere'
                }}
            >
                {postDigest}
            </div>
        );
    } else if (mode === 'modal') {
        return (
            <div
                className='tlw-mt-6'
                style={{
                    // This style is not yet supported by tailwind.
                    overflowWrap: 'anywhere'
                }}
            >
                {postDigest}
            </div>
        );
    }
}

function PostLink({postUrl, postDomain, postDeleted}) {
    if (postDeleted) {
        return null;
    }

    return (
        <a
            className='tlw-mt-5 tlw-inline-block tlw-overflow-hidden tlw-text-ellipsis tlw-text-sm tlw-text-gray-700'
            href={postUrl}
            target='_blank'
            rel='noreferrer'
        >
            <i className='fa fa-fw fa-external-link'/>
            View on {postDomain}
        </a>
    );
}

function PostDeleted({postDeleted}) {
    if (postDeleted) {
        return (
            <i>
                This post was deleted by the user.
            </i>
        );
    }
}

function PostCell({rowData}) {
    const postDigest = _.last(rowData.postDigest);
    const postAuthorName = _.last(rowData.postAuthorName);
    const postAuthorUsername = _.last(rowData.postAuthorUsername);
    const postTitle = _.last(rowData.postTitle);
    const postForumName = _.last(rowData.postForumName);
    const postSource = _.last(rowData.postSource);
    const postAuthorAvatarUrl = _.last(rowData.postAuthorAvatarUrl);
    const postAuthorLink = _.last(rowData.postAuthorLink);
    const postUrl = _.last(rowData.postUrl);
    const postDomain = _.last(rowData.postDomain);
    const postDeleted = _.last(rowData.postDeleted);

    return (
        <td
            className='tlw-p-4'>
            <div
                className='tlw-flex tlw-gap-4'>

                <PostSource
                    postAuthorAvatarUrl={postAuthorAvatarUrl}
                    postAuthorName={postAuthorName}
                    postSource={postSource}
                    postDomain={postDomain}
                />

                <div
                    className='tlw-flex-grow'
                >
                    <PostAuthor
                        postAuthorName={postAuthorName}
                        postAuthorUsername={postAuthorUsername}
                        postTitle={postTitle}
                        postForumName={postForumName}
                        postAuthorLink={postAuthorLink}
                        postDeleted={postDeleted}
                    />

                    <PostDigest
                        postDigest={postDigest}
                        mode={'cell'}
                        postDeleted={postDeleted}
                    />

                    <PostLink
                        postUrl={postUrl}
                        postDomain={postDomain}
                        postDeleted={postDeleted}
                    />

                    <PostDeleted
                        postDeleted={postDeleted}
                    />
                </div>

            </div>
        </td>
    );
}

function PostModal({formattersByFieldName, isModalVisible, onModalDismissed, postData, timezone}) {
    const postPublishedAt = _.last(postData.postPublishedAt);
    const postDigest = _.last(postData.postDigest);
    const postAuthorName = _.last(postData.postAuthorName);
    const postAuthorUsername = _.last(postData.postAuthorUsername);
    const postTitle = _.last(postData.postTitle);
    const postForumName = _.last(postData.postForumName);
    const postSource = _.last(postData.postSource);
    const postAuthorAvatarUrl = _.last(postData.postAuthorAvatarUrl);
    const postAuthorLink = _.last(postData.postAuthorLink);
    const postUrl = _.last(postData.postUrl);
    const postDomain = _.last(postData.postDomain);
    const postImpressions = _.last(postData.postImpressions);
    const sentiment = _.last(postData.postSentiment);
    const postDeleted = _.last(postData.postDeleted);
    const {
        sentimentDescription,
        impressionsDescription
    } = listeningDefinitions['posts-widget'] || {};

    return (
        <RIQModal
            title='Post Details'
            showModal={isModalVisible}
            onModalDismissed={onModalDismissed}>
            <div
                className='tlw-flex tlw-w-full'>
                <div
                    className='tlw-flex tlw-grow tlw-gap-7.5 tlw-p-7.5'>

                    <PostSource
                        postAuthorAvatarUrl={postAuthorAvatarUrl}
                        postAuthorName={postAuthorName}
                        postSource={postSource}
                        postDomain={postDomain}
                        isWideDisplay={true}
                    />

                    <div
                        className='tlw-flex-grow'
                    >
                        <PostAuthor
                            postAuthorName={postAuthorName}
                            postAuthorUsername={postAuthorUsername}
                            postTitle={postTitle}
                            postForumName={postForumName}
                            postAuthorLink={postAuthorLink}
                            postDeleted={postDeleted}
                        />

                        <div
                            className='tlw-whitespace-nowrap tlw-text-sm'>
                            {moment.tz(postPublishedAt, moment.ISO_8601, timezone).format('MMM D, YYYY')}
                        </div>

                        <PostDigest
                            postDigest={postDigest}
                            mode={'modal'}
                            postDeleted={postDeleted}
                        />

                        <PostLink
                            postUrl={postUrl}
                            postDomain={postDomain}
                            postDeleted={postDeleted}
                        />

                        <PostDeleted
                            postDeleted={postDeleted}
                        />
                    </div>

                </div>

                <div className='tlw-border-0 tlw-border-l tlw-border-solid tlw-border-gray-300 tlw-bg-gray-125'>

                    <div className='tlw-table tlw-border-collapse'>

                        <div className='tlw-table-row'>
                            <div className={`tlw-table-cell tlw-py-7 tlw-pl-7 tlw-text-right tlw-align-middle tlw-text-21 tlw-font-bold`}>

                                <RIQPopover
                                    triggerNode={formattersByFieldName.postImpressions.abbreviated(postImpressions)}
                                    placement='top'
                                >
                                    <div className='tlw-px-7 tlw-py-4.5'>
                                        <div
                                            className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                            {formattersByFieldName.postImpressions.expanded(postImpressions)}
                                        </div>
                                    </div>
                                </RIQPopover>
                            </div>
                            <div className='tlw-table-cell tlw-whitespace-nowrap tlw-px-8 tlw-align-middle tlw-text-sm tlw-font-bold tlw-uppercase tlw-text-gray-900'>
                                Potential Impressions
                                <RIQInfoPopover
                                    title='Potential Impressions'
                                    description={impressionsDescription}
                                />
                            </div>
                        </div>

                        <div className='tlw-table-row tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-300'>
                            <PostSentiment
                                sentiment={sentiment}
                                mode={'modal'}
                                postDeleted={postDeleted}
                            />
                            <div className='tlw-table-cell tlw-whitespace-nowrap tlw-px-8 tlw-align-middle tlw-text-sm tlw-font-bold tlw-uppercase tlw-text-gray-900'>
                                Sentiment
                                <RIQInfoPopover
                                    title='Sentiment'
                                    description={sentimentDescription}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </RIQModal>
    );
}

const emptyFallback = <Empty subject='posts'/>;

export default function PostsTableComponent({instanceId, limit, overrides, showOnlyPostsColumn, showSinglePostModals, pdfExportConfig, appSpecificExportConfig}) {

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [postModalData, setPostModalData] = useState({});

    const {
        title = `Posts`
    } = listeningDefinitions[instanceId] || {};

    const metricFilters = useListeningCurationFilterValues(instanceId, overrides);
    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig?.timePeriodStr
        }
    });

    const focusedInstanceId = useGetFocusedInstanceIdValue();
    metricFilters.listening.limit = limit ? limit : (focusedInstanceId === instanceId ? -1 : 3);

    const {
        sentimentDescription,
        impressionsDescription,
        publishedAtDescription
    } = listeningDefinitions['posts-widget'] || {};

    let columnsDefs = [{
        metrics: ['postPublishedAt'],
        description: publishedAtDescription,
        makeTableHeader: makeDefaultTableHeader,
        makeTableCell: _.partial(makePublishedAtTableCell, metricFilters.timePeriod.timezone)
    }, {
        metrics: ['postDigest', 'postAuthorName', 'postAuthorUsername', 'postTitle', 'postForumName', 'postSource', 'postAuthorAvatarUrl', 'postAuthorLink', 'postUrl', 'postDomain', 'postDeleted'],

        makeTableHeader: ({columnDef}) => {
            return (
                <MetricTableHeader
                    key={columnDef.idx}
                    title={title}
                />
            );
        },
        makeTableCell: ({columnDef, rowData}) => {
            return <PostCell
                key={columnDef.idx}
                rowData={rowData}
            />;
        }
    }, {
        metrics: ['postImpressions'],
        description: impressionsDescription,
        makeTableHeader: makeDefaultTableHeader,
        makeTableCell: makeDefaultTableCell
    }, {
        metrics: ['postSentiment', 'postDeleted'],
        description: sentimentDescription,
        makeTableHeader: makeDefaultTableHeader,
        makeTableCell: makePostSentimentTableCell
    }];

    if (showOnlyPostsColumn) {
        columnsDefs = [columnsDefs[1]];
    }

    _.each(columnsDefs, (columnDef, idx) => {
        columnDef.idx = idx;
    });

    const postFieldsToRequest = _(columnsDefs)
        .flatMap('metrics')
        .uniq()
        .union(['postId'])
        .value();

    const csvMetricFilters = JSON.parse(JSON.stringify(metricFilters));
    csvMetricFilters.listening.limit = -1;
    useSetExportConfig(instanceId, {
        title: title,
        supportedExportFormats: _.values(RivalIQ.enums.exportFormat),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        pdfExportConfig,
        csvExportConfig: {
            type: 'dataSourceMetricsGenerator',
            metricGroups: [
                _.map(_.without(postFieldsToRequest, 'postId'), field => {
                    return {
                        dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
                        dimensions: ['post'],
                        measure: field,
                        filters: csvMetricFilters
                    };
                })
            ]
        },
        appSpecificExportConfig
    });

    const metricRequests = _.reduce(postFieldsToRequest, (memo, field) => {
        if (!memo[field]) {
            memo[field] = {
                dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
                dimensions: ['post'],
                measure: field,
                filters: metricFilters
            };
        }

        return memo;
    }, {});

    function onRowClick(row) {
        setPostModalData(row);
        setIsModalVisible(true);
    }

    return (
        <MetricLoadingView
            emptyFallback={emptyFallback}
            instanceId={instanceId}
            metricRequests={metricRequests}>
            {
                (metricResults) => {
                    const collatedResults = RivalIQ.metrics.new.MetricResponse.groupResponsesByFixedDimensions(metricResults);

                    const formattersByFieldName = _.mapValues(metricRequests, (request) => {
                        const fieldName = request.measure;
                        return {
                            abbreviated: RivalIQ.metrics.new.MetricFormatter.getFormatter(request, metricResults[fieldName], RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated),
                            expanded: RivalIQ.metrics.new.MetricFormatter.getFormatter(request, metricResults[fieldName], RivalIQ.metrics.new.enums.MetricFormatTypes.expanded)
                        };
                    });

                    return (<>
                        <table className='tlw-w-full'>
                            <thead>
                                <tr className='tlw-bg-gray-150'>
                                    {_.map(columnsDefs, (columnDef) => {
                                        return columnDef.makeTableHeader({
                                            columnDef,
                                            metricRequests
                                        });
                                    })}
                                </tr>
                            </thead>

                            <tbody>
                                {_.map(collatedResults, (rowData) => {
                                    return (
                                        <tr
                                            key={_.last(rowData.postId)}
                                            className={`tlw-h-[47px] tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-200 hover_tlw-bg-blue-50 ${showSinglePostModals ? 'tlw-cursor-pointer' : ''}`}
                                            onClick={() => onRowClick(rowData)}>
                                            {_.map(columnsDefs, (columnDef, idx) => {
                                                return columnDef.makeTableCell({
                                                    columnDef,
                                                    metricRequests,
                                                    rowData: _.pick(rowData, columnDef.metrics),
                                                    formattersByFieldName
                                                });
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {showSinglePostModals && <PostModal
                            formattersByFieldName={formattersByFieldName}
                            isModalVisible={isModalVisible}
                            onModalDismissed={() => setIsModalVisible(false)}
                            postData={postModalData}
                            timezone={metricFilters.timePeriod.timezone}
                        />}
                    </>);
                }
            }
        </MetricLoadingView>
    );
}
